'use client';

import { useState } from 'react';
import { RetryingImage } from '@/components/baseImage/RetryingImage';
import { VideoPlayerProps } from '@/components/video-player/types';
import Icon from '@/components/icon/Icon';
import { Headline } from '@/components/headline/Headline';
import { usePathname } from 'next/navigation';
import clsx from 'clsx';
import { getImageUrl } from '../baseImage/util/getImageUrl';

const toYouTubeEmbedUrl = (
  rawUrl: string,
  {
    autoplay,
    controls,
  }: {
    autoplay: boolean;
    controls: boolean;
  },
) => {
  if (!rawUrl) return rawUrl;

  const ensureProtocol = (value: string) =>
    value.startsWith('http://') || value.startsWith('https://')
      ? value
      : `https://${value}`;

  const parseStartSeconds = (value: string) => {
    if (!value) return null;
    if (/^\d+$/.test(value)) return value;
    const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
    if (!match) return null;
    const hours = Number(match[1] || 0);
    const minutes = Number(match[2] || 0);
    const seconds = Number(match[3] || 0);
    return String(hours * 3600 + minutes * 60 + seconds);
  };

  try {
    const url = new URL(ensureProtocol(rawUrl));
    const host = url.hostname;
    const isYouTube =
      host.includes('youtube.com') ||
      host.includes('youtu.be') ||
      host.includes('youtube-nocookie.com');
    if (!isYouTube) return rawUrl;

    let videoId = '';
    if (host.includes('youtu.be')) {
      videoId = url.pathname.split('/')[1] || '';
    } else if (url.pathname.startsWith('/embed/')) {
      videoId = url.pathname.split('/')[2] || '';
    } else if (url.pathname.startsWith('/shorts/')) {
      videoId = url.pathname.split('/')[2] || '';
    } else {
      videoId = url.searchParams.get('v') || '';
    }

    if (!videoId) return rawUrl;

    const embedUrl = new URL(
      `https://www.youtube-nocookie.com/embed/${videoId}`,
    );
    embedUrl.searchParams.set('autoplay', autoplay ? '1' : '0');
    embedUrl.searchParams.set('controls', controls ? '1' : '0');

    const start = url.searchParams.get('start');
    const t = url.searchParams.get('t');
    const startSeconds = parseStartSeconds(start || t || '');
    if (startSeconds) embedUrl.searchParams.set('start', startSeconds);

    const list = url.searchParams.get('list');
    if (list) embedUrl.searchParams.set('list', list);
    const index = url.searchParams.get('index');
    if (index) embedUrl.searchParams.set('index', index);

    return embedUrl.toString();
  } catch {
    return rawUrl;
  }
};

export const VideoPlayer = ({
  title,
  introduction,
  placeholderImage,
  videoUrl,
  controls = true,
  aspectRatio = '16:9',
  ariaLabel = 'Video Player',
  className = '',
  classNameVideoWrapper,
}: VideoPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const rawPath = usePathname();
  const pathname = rawPath ? rawPath.replace(/\/+$/, '') : '';
  const isContentRoute =
    /^\/de\/(?:edu-talk|artikel|news|flash-talk|podcast)\/[^/]+$/.test(
      pathname,
    );

  // Calculate padding-top for aspect ratio
  const aspectRatioPadding = (aspectRatio: string) => {
    const [w, h] = aspectRatio.split(':').map(Number);
    if (!w || !h || isNaN(w) || isNaN(h)) {
      console.error('Invalid aspect ratio provided.');
      return '56.25%'; // Default to 16:9
    }
    return `${(h / w) * 100}%`;
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const fallbackSrc = '/fallbackImage/fallback-img.webp';
  const imageSrc = placeholderImage ? getImageUrl(placeholderImage) : null;
  const placeholderSrc = imageSrc || fallbackSrc;
  const embedUrl = toYouTubeEmbedUrl(videoUrl, {
    autoplay: true,
    controls,
  });
  const width = '100%';
  const height = isContentRoute ? undefined : '670px';

  return (
    <div
      data-component-id="COO6"
      className={clsx(
        'smzh-video-player container mx-auto w-full rounded-lg py-10',
        !isContentRoute && 'mx-auto',
        className,
      )}
    >
      <div
        className={`video-wrapper relative w-full max-w-full overflow-hidden ${classNameVideoWrapper}`}
        style={{
          ...(isContentRoute && {
            paddingTop: aspectRatioPadding(aspectRatio),
          }),
          width,
          height,
        }}
      >
        {!isPlaying ? (
          // The inner play <button> is the accessible control; the wrapper
          // click is a pointer-only convenience and stays out of the
          // accessibility tree's interactive semantics.
          <div
            className="video-placeholder absolute inset-0 cursor-pointer overflow-hidden rounded-xl"
            onClick={handlePlay}
          >
            <RetryingImage
              src={placeholderSrc}
              alt=""
              fill
              sizes="100vw"
              quality={50}
              className="object-cover object-center"
            />
            {/* <div className="absolute h-full w-full bg-black opacity-50"></div> Dark Layer */}
            <div className="absolute bottom-10 left-10 flex h-3/4 w-full flex-col justify-end gap-14 pr-32 md:bottom-14 md:left-14 lg:w-1/2 lg:pr-20 xl:w-1/3">
              <div className="hidden flex-col gap-4 text-white md:flex">
                {title && (
                  <Headline title={title} level="h2" variant="headingThree" />
                )}
                {introduction && <p className="">{introduction}</p>}
              </div>
              <button
                className={clsx(
                  'play-button flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-white md:h-20 md:w-20',
                  { 'mt-auto': !introduction && !title },
                )}
                aria-label={title ? `Play video: ${title}` : 'Play video'}
                onClick={handlePlay}
              >
                <Icon name="play" size={25} />
              </button>
            </div>
          </div>
        ) : (
          <iframe
            className="video-iframe absolute left-0 top-0 h-full w-full border-0"
            src={embedUrl}
            title={title}
            aria-label={ariaLabel}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          ></iframe>
        )}
      </div>
    </div>
  );
};
